import React, { type Dispatch, type SetStateAction } from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useUpdateAdditionalService } from 'src/features/parkingManagement/api/AdditionalServicesAPIs/useUpdateAdditionalService';
import {
    createAdditionalServicesFormSchema,
    type CreateAdditionalServicesFormSchemaValues,
} from 'src/features/parkingManagement/components/AdditionalServices/AddAdditionalServices';
import AdditionalServicesForm from 'src/features/parkingManagement/components/AdditionalServices/AdditionalServicesForm';
import { useDisplayedAdditionalServicesFormValues } from 'src/features/parkingManagement/providers/AdditionalServicesProvider';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { formatPrice } from 'src/features/reservations/utils/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { AdditionalServices } from 'src/features/parkingManagement/types/additionalServices';

type EditAdditionalServiceProps = {
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    selectedService: AdditionalServices;
    setSelectedService: Dispatch<SetStateAction<AdditionalServices | undefined>>;
    refetch: () => void;
};

const EditAdditionalService = ({
    setIsModalOpen,
    selectedService,
    setSelectedService,
    refetch,
}: EditAdditionalServiceProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { setDisplayedFormValues } = useDisplayedAdditionalServicesFormValues();

    const form = useForm<CreateAdditionalServicesFormSchemaValues>({
        resolver: zodResolver(createAdditionalServicesFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    React.useEffect(() => {
        if (selectedService) {
            const defaultValues = {
                name: selectedService.name,
                description: selectedService.description ?? undefined,
                type: selectedService.type,
                used_for: selectedService.used_for,
                reservation_types: selectedService.reservation_types ?? [],
                is_optional: selectedService.is_optional,
                is_enabled: selectedService.is_enabled,
                currency: selectedService.currency,
                tax_percent: selectedService.tax_percent,
                price_with_tax: selectedService.price_with_tax,
                price: selectedService.price,
                tax_amount: selectedService.tax_amount,
                valid_from: new Date(selectedService.valid_from),
            };
            form.reset(defaultValues);
            setDisplayedFormValues({
                tax_percent: (Number(selectedService.tax_percent) * 100).toFixed(2),
                price_with_tax: formatPrice(selectedService.price_with_tax),
                price: formatPrice(selectedService.price),
                tax_amount: formatPrice(selectedService.tax_amount),
            });
        }
    }, [selectedService]);

    const { mutate: updateService, isPending } = useUpdateAdditionalService({
        onSuccess: (_) => {
            void refetch();
            setSelectedService(undefined);
            setDisplayedFormValues({
                tax_percent: undefined,
                price_with_tax: undefined,
                price: undefined,
                tax_amount: undefined,
            });
            toast({
                title: formatMessage(parkingManagementMessages.serviceUpdated),
                description: formatMessage(parkingManagementMessages.serviceUpdatedSuccessfully, {
                    name: selectedService?.name,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: CreateAdditionalServicesFormSchemaValues) => {
        if (!parkingId || !selectedService) return;
        const { tax_amount, price, ...submitValues } = values;

        updateService({
            parkingId,
            id: selectedService?.id,
            ...submitValues,
        });
    };

    return (
        <Dialog open={true} onOpenChange={setIsModalOpen}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row items-center gap-2">
                        <SquareIcon icon={faEdit} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...parkingManagementMessages.editService} />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage {...parkingManagementMessages.editServiceDescription} />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AdditionalServicesForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1" onClick={() => form.reset()}>
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={!form.formState.isDirty || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...parkingManagementMessages.editService} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditAdditionalService;
